import { lazy, ReactNode, Suspense } from "react";
import PrivateRoute from "./PrivateRoute";
import { Backdrop, CircularProgress } from "@mui/material";
const NotFoundPage = lazy(() => import("../pages/notFoundPage/NotFoundPage"));
const UnauthorizedPage = lazy(() => import("../pages/unAuthorizedPage/UnauthorizedPage"));
const MainLayout = lazy(() => import("../layouts/MainLayout"));
const Dashboard = lazy(() => import("../pages/dashboard/Dashboard"));
const SettingForm = lazy(() => import("../pages/setting/settingForm/SettingForm"));
const AllSettings = lazy(() => import("../pages/setting/all/AllSettings"));
const ProcedureForm = lazy(() => import("../pages/procedureMaster/procedureForm/ProcedureForm"));
const AllAppointments = lazy(() => import("../pages/appointment/all/AllAppointments"));
const AppointmentCalendar = lazy(() => import("../pages/appointment/appointmentCalendar/AppointmentCalendar"));
const UpcomingConsultation = lazy(() => import("../pages/consultation/UpcomingConsultation"));
const UpcomingProcedure = lazy(() => import("../pages/procedure/upcoming/UpcomingProcedure"));
const UpcomingFirstProcedure = lazy(() => import("../pages/procedure/upcoming/UpcomingFirstProcedures"));
const ViewSetting = lazy(() => import("../pages/setting/view/ViewSetting"));
const ProcedureCalendar = lazy(() => import("../pages/procedureCalendar/ProcedureCalendar"));
const ConsultationCalendar = lazy(() => import("../pages/consultationCalendar/ConsultationCalendar"));
const AppointmentForm = lazy(() => import("../pages/appointment/appointmentForm/AppointmentForm"));
const AllProceduresMaster = lazy(() => import("../pages/procedureMaster/all/AllProcedures"));
const Invoice = lazy(() => import("../pages/invoice"));
const Patients = lazy(() => import("../pages/patients"));
const SinglePatient = lazy(() => import("../pages/patients/SinglePatient"));
const ForceChangePassword = lazy(() => import("../pages/forceChangePassword/ForceChangePassword"));
const BranchForm = lazy(() => import("../pages/branch/branchForm/BranchForm"));
const AllBranches = lazy(() => import("../pages/branch/all/AllBranches"));
const UserForm = lazy(() => import("../pages/user/userForm/UserForm"));
const AllUsers = lazy(() => import("../pages/user/all/AllUsers"));
const Consultation = lazy(() => import('../pages/consultation/Consultation'));
const ConsultationForm = lazy(() => import('../pages/consultation/ConsultationForm'));
const PastConsultation = lazy(() => import('../pages/consultation/PastConsultation'));
const Procedures = lazy(() => import('../pages/consultation/procedure/Procedures'));
const CreateProcedure = lazy(() => import('../pages/consultation/procedure/CreateProcedure'));
const Media = lazy(() => import("../pages/consultation/Media"));
const PaymentHistory = lazy(() => import("../pages/consultation/PaymentHistory"));
const AllProcedures = lazy(() => import("../pages/consultation/procedure/AllProcedure"));
const PastSessions = lazy(() => import("../pages/consultation/procedure/PastSessions"));
const AllPackage = lazy(() => import("../pages/consultation/package/AllPackage"));
const CreatePackage = lazy(() => import("../pages/consultation/package/CreatePackage"));
const SessionForm = lazy(() => import("../pages/consultation/session/SessionForm"));
const NetworkErrorPage = lazy(() => import("../pages/networkErrorPage/NetworkErrorPage"));
const TransactionHistory = lazy(() => import("../pages/transactionHistory"));


export interface RouteType {
  path: string;
  element?: ReactNode;
  children?: RouteType[];
}


const Loading = () => (
  <Backdrop
    sx={{
      color: (theme) => theme.palette.primary.contrastText,
      zIndex: (theme) => theme.zIndex.drawer + 1,
    }}
    open={true} // Ensure backdrop is always visible during loading
  >
    <CircularProgress color="inherit" />
  </Backdrop>
);

const MainRoutes: RouteType = {
  path: "",
  element:
    <Suspense fallback={<Loading />}>
      <MainLayout />
    </Suspense>
  ,
  children: [
    {
      path: '',
      element:
        <Suspense fallback={<Loading />}>
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        </Suspense>
    },
    {
      path: "consultation",
      children: [
        {
          path: "create/:caseId/:uuid/:page",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="create">
                <Consultation action={"create"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              path: "consultation-tab",
              element: <Suspense fallback={<Loading />}><PrivateRoute permissionKey={"consultation"} requiredPermission="create"><ConsultationForm action={"create"} permissionKey={"consultation"} /></PrivateRoute></Suspense>
            },
            {
              path: "past-consultation",
              element: <Suspense fallback={<Loading />}><PrivateRoute permissionKey={"consultation"} requiredPermission="view"><PastConsultation /></PrivateRoute></Suspense>,
            },
            {
              path: "procedures",
              element: <Suspense fallback={<Loading />}><PrivateRoute permissionKey={"procedure"} requiredPermission="view"><Procedures permissionKey={"procedure"} requiredPermission="view" /></PrivateRoute></Suspense>,
              children: [
                {
                  path: "procedure/create",
                  element: <Suspense fallback={<Loading />}><PrivateRoute permissionKey={"procedure"} requiredPermission="create">
                    <CreateProcedure action={"create"} requiredPermission="create" /></PrivateRoute></Suspense>,
                },
                {
                  path: "past-sessions",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"procedure"} requiredPermission="view"><PastSessions permissionKey={"procedure"} requiredPermission="view" /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "procedure/all",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"procedure"} requiredPermission="view"><AllProcedures permissionKey={"procedure"} requiredPermission="view" /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "procedure/edit/:procedureId",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"procedure"} requiredPermission="edit"><CreateProcedure permissionKey={"procedure"} requiredPermission="edit" action={"edit"} /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "procedure/view/:procedureId",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"procedure"} requiredPermission="show"><CreateProcedure permissionKey={"procedure"} requiredPermission="view" action={"view"} /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "session/edit/:sessionId/:procedureId",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"session"} requiredPermission="edit"><SessionForm permissionKey={"session"} action={"edit"} /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "session/view/:sessionId/:procedureId",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"session"} requiredPermission="view"><SessionForm permissionKey={"session"} action={"view"} viewOnly={true} /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "package/create",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"package"} requiredPermission="create"><CreatePackage /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "package/view/:packageId",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"package"} requiredPermission="view"><CreatePackage permissionKey={"package"} requiredPermission="view" action={"view"} /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "package/edit/:packageId",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"package"} requiredPermission="edit"><CreatePackage permissionKey={"package"} requiredPermission="edit" action={"edit"} /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "package/all",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"package"} requiredPermission="view"><AllPackage permissionKey={"package"} requiredPermission="view" /></PrivateRoute>
                    </Suspense>
                },
              ],
            },
            {
              path: "media",
              element: <Suspense fallback={<Loading />}><PrivateRoute permissionKey={"media"} requiredPermission="view">
                <Media />
              </PrivateRoute>
              </Suspense>,
            },
            {
              path: "payment-history",
              element: <Suspense fallback={<Loading />}>
                <PrivateRoute permissionKey={"payment"} secondaryKey={"payment-history"} requiredPermission="view">
                  <PaymentHistory />
                </PrivateRoute>
              </Suspense>,
            },
          ]
        },
        {
          path: "edit/:caseId/:uuid/:page",
          element: (
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"consultation"} requiredPermission="edit">
                <Consultation action={"edit"} permissionKey={"consultation"} />
              </PrivateRoute>
            </Suspense>
          ),
          children: [
            {
              path: "consultation-tab",
              element: <Suspense fallback={<Loading />}><PrivateRoute permissionKey={"consultation"} requiredPermission="edit"><ConsultationForm action={"edit"} permissionKey={"consultation"} /></PrivateRoute></Suspense>,
            },
            {
              path: "past-consultation",
              element: <Suspense fallback={<Loading />}><PrivateRoute permissionKey={"consultation"} requiredPermission="view"><PastConsultation /></PrivateRoute></Suspense>,
            },
            {
              path: "procedures",
              element: <Suspense fallback={<Loading />}><PrivateRoute permissionKey={"procedure"} requiredPermission="view"><Procedures permissionKey={"procedure"} requiredPermission="view" /></PrivateRoute></Suspense>,
              children: [
                {
                  path: "procedure/create",
                  element: <Suspense fallback={<Loading />}><PrivateRoute permissionKey={"procedure"} requiredPermission="create"><CreateProcedure action={"create"} requiredPermission="create" /></PrivateRoute></Suspense>,
                },
                {
                  path: "procedure/all",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"procedure"} requiredPermission="view"><AllProcedures permissionKey={"procedure"} requiredPermission="view" /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "past-sessions",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"procedure"} requiredPermission="view"><PastSessions permissionKey={"procedure"} requiredPermission="view" /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "procedure/edit/:procedureId",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"procedure"} requiredPermission="edit"><CreateProcedure permissionKey={"procedure"} requiredPermission="edit" action={"edit"} /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "procedure/view/:procedureId",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"procedure"} requiredPermission="view"><CreateProcedure permissionKey={"procedure"} requiredPermission="view" action={"view"} /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "session/edit/:sessionId/:procedureId",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"session"} requiredPermission="edit"><SessionForm permissionKey={"session"} action={"edit"} /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "session/view/:sessionId/:procedureId",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"session"} requiredPermission="view"><SessionForm permissionKey={"session"} action={"view"} viewOnly={true} /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "package/create",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"package"} requiredPermission="create"><CreatePackage /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "package/view/:packageId",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"package"} requiredPermission="view"><CreatePackage permissionKey={"package"} requiredPermission="view" action={"view"} /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "package/edit/:packageId",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"package"} requiredPermission="show"><CreatePackage permissionKey={"package"} requiredPermission="edit" action={"edit"} /></PrivateRoute>
                    </Suspense>
                },
                {
                  path: "package/all",
                  element:
                    <Suspense fallback={<Loading />}>
                      <PrivateRoute permissionKey={"package"} requiredPermission="view"><AllPackage permissionKey={"package"} requiredPermission="view" /></PrivateRoute>
                    </Suspense>
                },
              ],
            },
            {
              path: "media",
              element: <Suspense fallback={<Loading />}><PrivateRoute permissionKey={"media"} requiredPermission="view">
                <Media />
              </PrivateRoute>
              </Suspense>,
            },
            {
              path: "payment-history",
              element:
                <Suspense fallback={<Loading />}>
                  <PrivateRoute permissionKey={"payment"} secondaryKey={"payment-history"} requiredPermission="edit">
                    <PaymentHistory />
                  </PrivateRoute>
                </Suspense>,
            },
          ]
        },
      ],
    },
    {
      path: "user",
      children: [
        {
          path: "create",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"user"} requiredPermission="create">
                <UserForm action={"create"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view/:userId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"user"} requiredPermission="show">
                <UserForm action={"show"} viewOnly={true} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "edit/:userId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"user"} requiredPermission="edit">
                <UserForm action={"edit"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "all/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"user"} requiredPermission="view">
                <AllUsers permissionKey={"user"} requiredPermission="view" />
              </PrivateRoute>
            </Suspense>
        },
      ]
    },
    {
      path: "branch",
      children: [
        {
          path: "create",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"branch"} requiredPermission="create">
                <BranchForm action={"create"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view/:branchId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"branch"} requiredPermission="view">
                <BranchForm action={"view"} viewOnly={true} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "edit/:branchId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"branch"} requiredPermission="edit">
                <BranchForm action={"edit"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "all/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"branch"} requiredPermission="view">
                <AllBranches permissionKey={"branch"} requiredPermission="view" />
              </PrivateRoute>
            </Suspense>
        },
      ]
    },
    {
      path: "setting",
      children: [
        {
          path: "create",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"setting"} requiredPermission="create">
                <SettingForm action={"create"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view/:settingId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"setting"} requiredPermission="view">
                <SettingForm action={"view"} viewOnly={true} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "edit/:settingId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"setting"} requiredPermission="edit">
                <SettingForm action={"edit"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "all/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"setting"} requiredPermission="view">
                <AllSettings permissionKey={"setting"} requiredPermission="view" />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view-setting",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"setting"} requiredPermission="show">
                <ViewSetting />
              </PrivateRoute>
            </Suspense>
        },
      ]
    },
    {
      path: "procedure",
      children: [
        {
          path: "create",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"procedure-master"} requiredPermission="create">
                <ProcedureForm action={"create"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view/:procedureId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"procedure-master"} requiredPermission="show">
                <ProcedureForm action={"show"} viewOnly={true} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "edit/:procedureId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"procedure-master"} requiredPermission="edit">
                <ProcedureForm action={"edit"} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "all/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"procedure-master"} requiredPermission="show">
                <AllProceduresMaster permissionKey={"procedure-master"} requiredPermission="show" />
              </PrivateRoute>
            </Suspense>
        },
      ]
    },
    {
      path: "appointment",
      children: [
        {
          path: "all/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"appointment"} requiredPermission="view">
                <AllAppointments permissionKey={"appointment"} requiredPermission="view" />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "create",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"calendar"} requiredPermission="view">
                <AppointmentCalendar action={"create"} permissionKey={"appointment"} requiredPermission="create" />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "view/:appointmentId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"appointment"} requiredPermission="view">
                <AppointmentForm action={"view"} viewOnly={true} />
              </PrivateRoute>
            </Suspense>
        },
        {
          path: "edit/:appointmentId/:page",
          element:
            <Suspense fallback={<Loading />}>
              <PrivateRoute permissionKey={"appointment"} requiredPermission="edit">
                <AppointmentForm action={"edit"} />
              </PrivateRoute>
            </Suspense>
        },
      ]
    },
    {
      path: "upcoming",
      children: [
        {
          path: "consultation/:page",
          element: <Suspense fallback={<Loading />}>
            <PrivateRoute permissionKey={"upcoming"} requiredPermission="view">
              <UpcomingConsultation permissionKey={"consultation"} requiredPermission="view" />
            </PrivateRoute>
          </Suspense>
        },
        {
          path: "consultation/:page/consultation-calendar/:appointmentId",
          element: <Suspense fallback={<Loading />}>
            <PrivateRoute permissionKey={"upcoming"} requiredPermission="view">
              <ConsultationCalendar />
            </PrivateRoute>
          </Suspense>
        },
        {
          path: "procedure/:page",
          element: <Suspense fallback={<Loading />}>
            <PrivateRoute permissionKey={"upcoming"} requiredPermission="view">
              <UpcomingProcedure permissionKey={"procedure"} requiredPermission="view" />
            </PrivateRoute>
          </Suspense>
        },
        {
          path: "first-procedure/:page",
          element: <Suspense fallback={<Loading />}>
            <PrivateRoute permissionKey={"upcoming"} requiredPermission="view">
              <UpcomingFirstProcedure permissionKey={"procedure"} requiredPermission="view" />
            </PrivateRoute>
          </Suspense>
        },
        {
          path: "procedure/:page/procedure-calendar/:dateRange",
          element: <Suspense fallback={<Loading />}>
            <PrivateRoute permissionKey={"upcoming"} requiredPermission="view">
              <ProcedureCalendar />
            </PrivateRoute>
          </Suspense>
        },
      ]
    },
    {
      path: "invoice/all/:page",
      element: <Suspense fallback={<Loading />}>
        <PrivateRoute permissionKey={"invoice"} requiredPermission="view">
          <Invoice />
        </PrivateRoute>
      </Suspense>
    },
    {
      path: "transactions/all/:page",
      element: <Suspense fallback={<Loading />}>
        <PrivateRoute permissionKey={"invoice"} requiredPermission="view">
          <TransactionHistory />
        </PrivateRoute>
      </Suspense>
    },
    {
      path: "patients/all/:page",
      element: <Suspense fallback={<Loading />}>
        <PrivateRoute permissionKey={"patient"} requiredPermission="view">
          <Patients />
        </PrivateRoute>
      </Suspense>
    },
    {
      path: "patient/:patientId/:page",
      element: <Suspense fallback={<Loading />}>
        <PrivateRoute permissionKey={"patient"} requiredPermission="view">
          <SinglePatient />
        </PrivateRoute>
      </Suspense>
    },
  ],
};

export default MainRoutes;


export const OtherRoutes: RouteType = {
  path: '',
  children: [
    {
      path: 'unauthorized',
      element: <Suspense fallback={<Loading />}>
        <UnauthorizedPage />
      </Suspense>
    },
    {
      path: 'not-found',
      element: <Suspense fallback={<Loading />}>
        <NotFoundPage />
      </Suspense>
    },
    {
      path: 'network-error',
      element: <Suspense fallback={<Loading />}>
        <NetworkErrorPage />
      </Suspense>
    },
    {
      path: 'change-password',
      element: <Suspense fallback={<Loading />}>
        <PrivateRoute>
          <ForceChangePassword />
        </PrivateRoute>
      </Suspense>
    },
    {
      path: '*',
      element: <Suspense fallback={<Loading />}>
        <NotFoundPage />
      </Suspense>
    },
  ],
}

